import { GetStateStorage } from "common/storage";
import React, { useState, useRef, useEffect } from "react";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import { Button } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import { UploadBackPagePhotoAPI } from "services/agent";

const normalizeAreas = (areas) =>
  (Array.isArray(areas) ? areas : [])
    .filter((area) => area && (area.mlsNeighborhood || area.mlsAreaID))
    .map((area) => ({
      mlsAreaID: area.mlsAreaID,
      mlsNeighborhood: area.mlsNeighborhood || area.mlsAreaID,
    }));

const BackPagePhotoUploadButton = ({
  currentArea = {},
  mlsAreas = [],
  onUploadSuccess = () => {},
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreaID, setSelectedAreaID] = useState(null);
  const [formData, setFormData] = useState({
    file: null,
    applyScope: "this", // "all" | "this" | null
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dialogContentRef = useRef(null);

  const hasMultipleAreas = availableAreas.length > 1;
  const selectedArea =
    availableAreas.find((area) => area.mlsAreaID === selectedAreaID) || null;
  const areaLabel =
    selectedArea?.mlsNeighborhood ||
    currentArea.mlsNeighborhood ||
    currentArea.mlsAreaID ||
    "this area";

  const resolveAvailableAreas = () => {
    const fromProps = normalizeAreas(mlsAreas);
    if (fromProps.length > 0) {
      return fromProps;
    }
    const stateData = GetStateStorage();
    const agentData = (stateData || {}).userData || {};
    return normalizeAreas(agentData.navbarInfo);
  };

  useEffect(() => {
    if (message.type && dialogContentRef.current) {
      const scrollable = dialogContentRef.current.parentElement;
      if (scrollable) {
        scrollable.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [message.type]);

  const openDialog = () => {
    const areas = resolveAvailableAreas();
    const multi = areas.length > 1;
    const initialAreaID =
      currentArea.mlsAreaID || (areas[0] && areas[0].mlsAreaID) || null;
    setAvailableAreas(areas);
    setSelectedAreaID(initialAreaID);
    setFormData({
      file: null,
      applyScope: multi ? "all" : "this",
    });
    setMessage({ type: "", text: "" });
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      file: null,
      applyScope: "this",
    });
    setAvailableAreas([]);
    setSelectedAreaID(null);
    setMessage({ type: "", text: "" });
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const setApplyScope = (scope) => {
    setFormData((prev) => ({
      ...prev,
      applyScope: prev.applyScope === scope ? null : scope,
    }));
    setMessage({ type: "", text: "" });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
      setMessage({ type: "", text: "" });
    } else {
      setMessage({
        type: "error",
        text: "Please select an image file only (JPG or PNG).",
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        setFormData((prev) => ({
          ...prev,
          file: file,
        }));
        setMessage({ type: "", text: "" });
      } else {
        setMessage({
          type: "error",
          text: "Please drop an image file only (JPG or PNG).",
        });
      }
    }
  };

  const downloadSpecsPDF = () => {
    const pdfUrl =
      "https://d2onbxnrte2nim.cloudfront.net/non-covers/ListGen-2026_CUSTOM-BACK_Cover-SPECs.pdf";
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "ListGen-2026_Custom-BACK_Cover_HalfPage-SPECs.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resolveImagePath = (data) => {
    if (!data) return null;
    if (typeof data === "string") return data;
    return (
      data.backPagePhotoURL || data.photoURL || data.url || data.path || null
    );
  };

  const buildUploadFileName = (file, listgenUserID, areaID) => {
    const originalName = file.name || "back-page-photo.jpg";
    const extMatch = originalName.match(/\.([^.]+)$/);
    const ext = (extMatch ? extMatch[1] : "jpg").toLowerCase();
    // MMDDTHHMMSSZ (year removed; no dashes/underscores)
    const timestamp = new Date()
      .toISOString()
      .slice(4)
      .replace(/[-:]/g, "")
      .replace(/\.\d+Z$/, "Z");
    const safeUserID = String(listgenUserID || "unknown").replace(
      /[^a-zA-Z0-9]/g,
      "",
    );
    const safeAreaID = String(areaID === "-" ? "ALL" : areaID || "ALL").replace(
      /[^a-zA-Z0-9]/g,
      "",
    );
    return `${safeUserID}${safeAreaID}${timestamp}.${ext}`;
  };

  const submitUpload = async () => {
    if (!formData.file) {
      setMessage({
        type: "error",
        text: "Please select an image to upload.",
      });
      return;
    }

    const applyScope = hasMultipleAreas ? formData.applyScope : "this";

    if (hasMultipleAreas && !applyScope) {
      setMessage({
        type: "error",
        text: "Please select either all areas or only one area before uploading.",
      });
      return;
    }

    if (applyScope === "this" && !selectedAreaID) {
      setMessage({
        type: "error",
        text: "Please select which area this photo should apply to.",
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    const stateData = GetStateStorage();
    const agentData = (stateData || {}).userData || {};
    const listgenUserID = agentData.listgenUserID;
    const applyToAllAreas = applyScope === "all";
    const areaID = applyToAllAreas ? "-" : selectedAreaID || "-";
    const uploadAreaLabel = applyToAllAreas
      ? "all areas"
      : availableAreas.find((area) => area.mlsAreaID === areaID)
          ?.mlsNeighborhood || areaLabel;
    const uploadFileName = buildUploadFileName(
      formData.file,
      listgenUserID,
      areaID,
    );
    const fileToUpload = new File([formData.file], uploadFileName, {
      type: formData.file.type,
      lastModified: formData.file.lastModified,
    });

    try {
      const data = await UploadBackPagePhotoAPI(
        listgenUserID,
        areaID,
        fileToUpload,
      );
      const imagePath = resolveImagePath(data);

      if (!imagePath) {
        setMessage({
          type: "error",
          text: "Upload succeeded but no image path was returned. Please try again.",
        });
        return;
      }

      setMessage({
        type: "success",
        text: applyToAllAreas
          ? "Custom back page photo uploaded for all of your areas."
          : `Custom back page photo uploaded for ${uploadAreaLabel}.`,
      });

      onUploadSuccess(imagePath, {
        areaID,
        applyToAllAreas,
      });

      setTimeout(() => {
        closeDialog();
      }, 1200);
    } catch (error) {
      setMessage({
        type: "error",
        text:
          typeof error === "string"
            ? error
            : "Failed to upload back page photo. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
                    @keyframes successPulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.02); }
                        100% { transform: scale(1); }
                    }
                `}
      </style>
      <div
        className="backpage-upload-button-admin"
        onClick={openDialog}
        role="button"
        tabIndex={0}
      >
        <p>ADMIN:</p>
        <p>Upload Custom Back Page</p>
      </div>

      <MailProDialog
        open={isDialogOpen}
        onClose={closeDialog}
        aria-labelledby="back-page-upload-dialog-title"
      >
        <MailProDialogHeader>
          <h2
            id="back-page-upload-dialog-title"
            style={{
              fontFamily: "objektiv-mk2, sans-serif",
              color: "white",
              fontSize: "18px",
              fontWeight: "500",
              textAlign: "center",
              marginTop: "8px",
              lineHeight: "1.4",
              display: "block",
            }}
          >
            <span style={{ display: "block" }}>
              ADMIN: Optimized Custom Back Page
            </span>
            <span style={{ display: "block" }}>Photo Upload</span>
          </h2>
        </MailProDialogHeader>
        <MailProDialogBody>
          <div
            ref={dialogContentRef}
            style={{
              padding: "20px",
              fontFamily: "objektiv-mk2, sans-serif",
              position: "relative",
            }}
          >
            {message.type && (
              <div
                style={{
                  backgroundColor:
                    message.type === "success" ? "#d4edda" : "#f8d7da",
                  border: `1px solid ${
                    message.type === "success" ? "#c3e6cb" : "#f5c6cb"
                  }`,
                  color: message.type === "success" ? "#155724" : "#721c24",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "20px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  animation:
                    message.type === "success"
                      ? "successPulse 0.6s ease-in-out"
                      : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <div
                    style={{
                      backgroundColor:
                        message.type === "success" ? "#28a745" : "#dc3545",
                      color: "white",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "12px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      flexShrink: 0,
                      minWidth: "24px",
                      minHeight: "24px",
                    }}
                  >
                    {message.type === "success" ? "✓" : "✗"}
                  </div>
                  <span
                    style={{
                      fontWeight: "500",
                      fontFamily: "objektiv-mk2, sans-serif",
                      fontSize: message.type === "success" ? "16px" : "14px",
                    }}
                  >
                    {message.text}
                  </span>
                </div>
              </div>
            )}

            {hasMultipleAreas && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    fontFamily: "objektiv-mk2, sans-serif",
                    fontSize: "14px",
                  }}
                >
                  Apply this photo to:
                </label>
                <div
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fafafa",
                    marginBottom: "12px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "500",
                      fontFamily: "objektiv-mk2, sans-serif",
                      fontSize: "14px",
                      cursor: "pointer",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.applyScope === "all"}
                      onChange={() => setApplyScope("all")}
                      style={{ cursor: "pointer" }}
                    />
                    All areas ({availableAreas.length})
                  </label>
                  {formData.applyScope === "all" && (
                    <div
                      style={{
                        margin: "0 0 12px 24px",
                        fontSize: "13px",
                        color: "#555",
                        fontFamily: "objektiv-mk2, sans-serif",
                      }}
                    >
                      {availableAreas.map((area) => (
                        <div key={area.mlsAreaID} style={{ marginBottom: "4px" }}>
                          • {area.mlsNeighborhood}
                        </div>
                      ))}
                    </div>
                  )}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "500",
                      fontFamily: "objektiv-mk2, sans-serif",
                      fontSize: "14px",
                      cursor: "pointer",
                      marginBottom: formData.applyScope === "this" ? "10px" : 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.applyScope === "this"}
                      onChange={() => setApplyScope("this")}
                      style={{ cursor: "pointer" }}
                    />
                    Only one area
                  </label>
                  {formData.applyScope === "this" &&
                    availableAreas.map((area) => (
                      <label
                        key={area.mlsAreaID}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          margin: "0 0 8px 24px",
                          fontFamily: "objektiv-mk2, sans-serif",
                          fontSize: "14px",
                          cursor: "pointer",
                          wordBreak: "break-word",
                        }}
                      >
                        <input
                          type="radio"
                          name="backpage-single-area"
                          checked={selectedAreaID === area.mlsAreaID}
                          onChange={() => setSelectedAreaID(area.mlsAreaID)}
                          style={{ flexShrink: 0 }}
                        />
                        {area.mlsNeighborhood}
                      </label>
                    ))}
                </div>
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                    fontFamily: "objektiv-mk2, sans-serif",
                  }}
                >
                  {formData.applyScope === "all"
                    ? 'Uploads with areaID "-" and updates every area for this agent.'
                    : formData.applyScope === "this"
                      ? `Uploads for ${areaLabel} only.`
                      : "Select one option above."}
                </p>
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Upload Admin Optimized Custom Back Page Photo:
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                style={{
                  width: "95%",
                  padding: "20px",
                  border: `2px dashed ${isDragOver ? "#4e85af" : "#666"}`,
                  borderRadius: "8px",
                  backgroundColor: isDragOver ? "#f0f8ff" : "#fafafa",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontFamily: "objektiv-mk2, sans-serif",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "100px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "4px",
                  }}
                >
                  Drag & Drop Custom Back Page Photo here or click to browse
                </div>
                <div style={{ color: "#999", fontSize: "12px" }}>
                  Only JPG or PNG files are allowed
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              {formData.file && (
                <p
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    color: "#666",
                    fontFamily: "objektiv-mk2, sans-serif",
                  }}
                >
                  Selected: {formData.file.name}
                </p>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                onClick={closeDialog}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontFamily: "objektiv-mk2, sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitUpload}
                disabled={isSubmitting || !formData.file}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor: "#4e85af",
                  color: "white",
                  cursor:
                    isSubmitting || !formData.file ? "not-allowed" : "pointer",
                  opacity: isSubmitting || !formData.file ? 0.6 : 1,
                  fontFamily: "objektiv-mk2, sans-serif",
                  marginLeft: "-32px",
                }}
              >
                {isSubmitting ? "Uploading..." : "Upload"}
              </button>
              <div></div>
            </div>
          </div>
        </MailProDialogBody>
      </MailProDialog>
    </>
  );
};

export default React.memo(BackPagePhotoUploadButton);
